import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  BookOpen,
  Target,
  Trophy,
  Sparkles,
  Loader2,
  Mail,
  CreditCard,
  UserPlus
} from 'lucide-react';
import axios from 'axios';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { usePublicSettings } from '../contexts/PublicSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCmsContent } from '../hooks/useCmsContent';
import { parseHighlight } from '../utils/richText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const questions = [
  {
    id: 1,
    category: 'Grammar',
    question: 'She ___ to the gym every morning.',
    options: ['go', 'goes', 'going', 'gone']
  },
  {
    id: 2,
    category: 'Grammar',
    question: 'I ___ finished my homework when you called.',
    options: ["haven't", "hadn't", "didn't", "wasn't"]
  },
  {
    id: 3,
    category: 'Vocabulary',
    question: 'Choose the word that means "to make something larger or more extensive":',
    options: ['Reduce', 'Expand', 'Contract', 'Minimize']
  },
  {
    id: 4,
    category: 'Grammar',
    question: 'If I ___ you, I would apologize.',
    options: ['am', 'was', 'were', 'be']
  },
  {
    id: 5,
    category: 'Reading',
    question: '"The meeting has been postponed." This means the meeting has been:',
    options: ['Cancelled', 'Delayed', 'Started', 'Confirmed']
  },
  {
    id: 6,
    category: 'Grammar',
    question: 'By next year, she ___ at the company for ten years.',
    options: ['will work', 'will have worked', 'works', 'worked']
  },
  {
    id: 7,
    category: 'Vocabulary',
    question: 'The opposite of "optimistic" is:',
    options: ['Hopeful', 'Pessimistic', 'Realistic', 'Neutral']
  },
  {
    id: 8,
    category: 'Grammar',
    question: 'The report ___ by the manager yesterday.',
    options: ['reviewed', 'was reviewed', 'is reviewed', 'reviewing']
  },
  {
    id: 9,
    category: 'Vocabulary',
    question: '"She has a keen eye for detail." The word "keen" means:',
    options: ['Poor', 'Sharp', 'Lazy', 'Slow']
  },
  {
    id: 10,
    category: 'Grammar',
    question: 'Neither the teacher nor the students ___ aware of the change.',
    options: ['was', 'were', 'is', 'being']
  }
];

const levelResults = {
  beginner: {
    level: 'A1-A2',
    title: "You're at a Beginner / Elementary Level",
    description: "You're building your foundation. A structured and supportive learning plan will help you grow in confidence and consistency.",
    plan: 'Starter Plan / General English',
    icon: BookOpen,
    color: 'bg-[#4CAF50]',
    badge: 'badge-green'
  },
  intermediate: {
    level: 'B1',
    title: "You're at an Intermediate Level",
    description: "You already have a working foundation. Now is the perfect time to improve fluency, accuracy, and confidence in real communication.",
    plan: 'Intermediate Plan',
    icon: Target,
    color: 'bg-[#1A2E6E]',
    badge: 'badge-navy'
  },
  upperIntermediate: {
    level: 'B2',
    title: "You're at an Upper-Intermediate Level",
    description: "You're ready to refine your communication, improve precision, and use English more confidently for real-world and professional situations.",
    plan: 'Professional / Advanced Progress Plan',
    icon: Trophy,
    color: 'bg-[#F5A623]',
    badge: 'badge-orange'
  },
  advanced: {
    level: 'C1-C2',
    title: "You're at an Advanced Level",
    description: "You already communicate with confidence. Now the focus is refinement, precision, sophistication, and advanced fluency.",
    plan: 'Advanced / Intensive Plan',
    icon: Sparkles,
    color: 'bg-[#D72B2B]',
    badge: 'badge-red'
  }
};

const LEVEL_KEY_BY_CODE = {
  'A1-A2': 'beginner',
  'B1': 'intermediate',
  'B2': 'upperIntermediate',
  'C1-C2': 'advanced',
};

export const LevelTest = () => {
  const { executeRecaptcha } = usePublicSettings();
  const { isAuthenticated, isStudent } = useAuth();
  const { t } = useLanguage();
  const cms = useCmsContent('leveltest');

  const heroBadge = cms.get('hero', 'badge', t('levelTest.hero.badge'));
  const heroTitle = cms.get('hero', 'title', t('levelTest.hero.title'));
  const heroBody  = cms.get('hero', 'body',  t('levelTest.hero.body'));
  const emailBadge = cms.get('email_step', 'badge', t('levelTest.emailStep.badge'));
  const emailTitle = cms.get('email_step', 'title', t('levelTest.emailStep.title'));
  const emailBody  = cms.get('email_step', 'body',  t('levelTest.emailStep.body'));

  // Load questions from backend; fall back to hardcoded defaults
  const [activeQuestions, setActiveQuestions] = useState(questions);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/level-test-questions`)
      .then(res => {
        if (res.data.length > 0) {
          setActiveQuestions(res.data.map(q => ({
            id: q.id,
            category: q.category,
            question: q.text,
            options: q.options,
          })));
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const [testStarted, setTestStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [collectingLead, setCollectingLead] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [email, setEmail] = useState('');
  const [savingResult, setSavingResult] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [serverResult, setServerResult] = useState(null);

  const rawResult = serverResult || {
    score: 0,
    totalQuestions: activeQuestions.length,
    ...levelResults.beginner,
  };
  const levelKey = LEVEL_KEY_BY_CODE[rawResult.level] || 'beginner';
  const result = {
    ...rawResult,
    title: t(`levelTest.results.${levelKey}.title`),
    description: t(`levelTest.results.${levelKey}.description`),
    plan: t(`levelTest.results.${levelKey}.plan`),
  };

  const handleAnswer = (optionIndex) => {
    setAnswers({ ...answers, [currentQuestion]: optionIndex });
  };

  const handleNext = () => {
    if (currentQuestion < activeQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      return;
    }

    setCollectingLead(true);
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    setSavingResult(true);
    setLeadError('');

    try {
      const recaptchaToken = await executeRecaptcha('level_test');
      const response = await axios.post(`${API}/public/level-test`, {
        email,
        answers: activeQuestions.map((question, index) => ({
          question_id: String(question.id),
          selected_answer_index: answers[index],
        })),
        recaptcha_token: recaptchaToken
      });
      setServerResult({
        score: response.data.score,
        totalQuestions: response.data.total_questions,
        level: response.data.level,
        plan: response.data.recommended_plan,
      });
      setShowResult(true);
    } catch (error) {
      setLeadError(error.response?.data?.detail || t('levelTest.leadErrorFallback'));
    } finally {
      setSavingResult(false);
    }
  };

  const progress = ((currentQuestion + 1) / activeQuestions.length) * 100;

  if (!testStarted) {
    return (
      <main className="pt-24 min-h-screen bg-cream-gradient" data-testid="level-test-page">
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto text-center"
            >
              <span className="badge-red mb-4 inline-block">
                {heroBadge}
              </span>
              <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
                {parseHighlight(heroTitle)}
              </h1>
              <p className="text-lg text-[#1A2E6E]/70 mb-8">
                {heroBody}
              </p>

              <div className="glass-card p-6 sm:p-8 text-left mb-8 border-t-4 border-t-[#4CAF50]">
                <h3 className="font-sora font-semibold text-lg text-[#1A2E6E] mb-4">
                  {t('levelTest.assessedHeading')}
                </h3>
                <ul className="space-y-3">
                  {[
                    { text: t('levelTest.categoryGrammar'), color: 'text-[#D72B2B]' },
                    { text: t('levelTest.categoryVocabulary'), color: 'text-[#4CAF50]' },
                    { text: t('levelTest.categoryReadingComprehension'), color: 'text-[#F5A623]' },
                    { text: t('levelTest.categoryPracticalCommunication'), color: 'text-[#1A2E6E]' }
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle size={18} className={item.color} />
                      <span className="text-[#1A2E6E]/80">{item.text}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-sm text-[#1A2E6E]/60">
                  {t('levelTest.durationNote')}
                </p>
              </div>

              <button
                onClick={() => setTestStarted(true)}
                className="btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                data-testid="start-test-btn"
              >
                {t('levelTest.startButton')} <ArrowRight size={18} />
              </button>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  if (collectingLead && !showResult) {
    return (
      <main className="pt-24 min-h-screen bg-watercolor-soft" data-testid="level-test-lead-capture">
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto"
            >
              <div className="glass-card p-6 sm:p-8 md:p-10 border-t-4 border-t-[#D72B2B]">
                <div className="w-16 h-16 rounded-2xl bg-[#D72B2B]/10 flex items-center justify-center mx-auto mb-6">
                  <Mail size={30} className="text-[#D72B2B]" />
                </div>
                <div className="text-center mb-8">
                  <span className="badge-red inline-block mb-4">{emailBadge}</span>
                  <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
                    {parseHighlight(emailTitle)}
                  </h2>
                  <p className="text-[#1A2E6E]/70 leading-relaxed">
                    {emailBody}
                  </p>
                </div>

                <form onSubmit={handleLeadSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="level-test-email" className="text-[#1A2E6E] font-medium mb-2 block">
                      {t('levelTest.emailLabel')}
                    </Label>
                    <Input
                      id="level-test-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder={t('levelTest.emailPlaceholder')}
                      required
                      data-testid="level-test-email-input"
                      className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                    />
                  </div>

                  {leadError && (
                    <p className="text-sm text-[#D72B2B]">{leadError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={savingResult}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="level-test-save-result-btn"
                  >
                    {savingResult ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {t('levelTest.saving')}
                      </>
                    ) : (
                      <>
                        {t('levelTest.showResult')} <ArrowRight size={18} />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setCollectingLead(false)}
                    className="w-full text-[#1A2E6E]/70 hover:text-[#1A2E6E] transition-colors"
                    data-testid="level-test-review-answers-btn"
                  >
                    {t('levelTest.reviewAnswers')}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  if (showResult) {
    const ResultIcon = result.icon;

    return (
      <main className="pt-24 min-h-screen bg-watercolor-soft" data-testid="level-test-result">
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className={`w-20 h-20 rounded-2xl ${result.color} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                <ResultIcon size={40} className="text-white" />
              </div>

              <span className={`${result.badge} inline-block mb-4`}>
                {result.level}
              </span>

              <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
                {result.title}
              </h2>

              <p className="text-lg text-[#1A2E6E]/70 mb-4">
                {result.description}
              </p>

              <p className="text-sm text-[#1A2E6E]/60 mb-8">
                {t('levelTest.scoreLine', { score: result.score, total: result.totalQuestions })}
              </p>

              <div className="glass-card p-6 mb-8 border-t-4 border-t-[#F5A623]">
                <p className="text-sm text-[#1A2E6E]/60 mb-2">{t('levelTest.recommendedPlanLabel')}</p>
                <p className="font-sora font-semibold text-xl text-[#1A2E6E]">
                  {result.plan}
                </p>
              </div>

              {/* CTAs: context-aware based on auth state */}
              {isAuthenticated && isStudent ? (
                // Logged-in student → go directly to their options page
                <div className="flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap">
                  <Link
                    to="/student/pending"
                    className="btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                    data-testid="view-packages-btn"
                  >
                    <CreditCard size={18} /> {t('levelTest.seePlanOptions')} <ArrowRight size={18} />
                  </Link>
                </div>
              ) : (
                // Anonymous visitor → register (carries them into the funnel)
                <div className="flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap">
                  <Link
                    to="/register"
                    className="btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                    data-testid="view-packages-btn"
                  >
                    <UserPlus size={18} /> {t('levelTest.createAccountCta')} <ArrowRight size={18} />
                  </Link>
                  <Link
                    to="/book-trial"
                    className="btn-secondary w-full text-center sm:w-auto"
                    data-testid="result-book-trial-btn"
                  >
                    {t('levelTest.bookTrialCta')}
                  </Link>
                </div>
              )}
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  const currentQ = activeQuestions[currentQuestion];
  const categoryColors = {
    Grammar: 'badge-navy',
    Vocabulary: 'badge-green',
    Reading: 'badge-orange'
  };
  const categoryLabels = {
    Grammar: t('levelTest.categoryBadge.Grammar'),
    Vocabulary: t('levelTest.categoryBadge.Vocabulary'),
    Reading: t('levelTest.categoryBadge.Reading'),
  };

  return (
    <main className="pt-24 min-h-screen bg-paper-lines" data-testid="level-test-questions">
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-[#1A2E6E]/60">
                  {t('levelTest.questionCounter', { current: currentQuestion + 1, total: activeQuestions.length })}
                </span>
                <span className={`${categoryColors[currentQ.category] || 'badge-navy'} text-xs`}>
                  {categoryLabels[currentQ.category] || currentQ.category}
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestion}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="glass-card p-6 sm:p-8 border-t-4 border-t-[#1A2E6E]"
              >
                <h2 className="font-sora text-xl font-semibold text-[#1A2E6E] mb-6">
                  {currentQ.question}
                </h2>

                <div className="space-y-3">
                  {currentQ.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswer(index)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        answers[currentQuestion] === index
                          ? 'border-[#1A2E6E] bg-[#1A2E6E]/5'
                          : 'border-[#1A2E6E]/10 hover:border-[#1A2E6E]/30 bg-white'
                      }`}
                      data-testid={`option-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-sm font-medium ${
                          answers[currentQuestion] === index
                            ? 'border-[#1A2E6E] bg-[#1A2E6E] text-white'
                            : 'border-[#1A2E6E]/30 text-[#1A2E6E]/50'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="text-[#1A2E6E]">{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between mt-8">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="flex w-full items-center justify-center gap-2 text-[#1A2E6E]/70 transition-colors hover:text-[#1A2E6E] disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
                data-testid="prev-question-btn"
              >
                <ArrowLeft size={18} /> {t('levelTest.previous')}
              </button>

              <button
                onClick={handleNext}
                disabled={answers[currentQuestion] === undefined}
                className="btn-primary inline-flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                data-testid="next-question-btn"
              >
                {currentQuestion === activeQuestions.length - 1 ? t('levelTest.continueButton') : t('levelTest.next')}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LevelTest;
